import { Injectable, inject } from '@angular/core';
import { HttpContextToken, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { finalize, Observable, of } from 'rxjs';
import { catchError, first, map, switchMap } from 'rxjs/operators';
import { decodeJwt } from 'jose';
import { MessageService } from 'primeng/api';
import { AuthService, SecurityProfileAuthToken } from '../services';
import { CustomUiHeaders } from '../models';
import { SpinnerService } from '../shared';
import { APP_ENV } from 'apps/cmg-clear2-ui/src/environments/environment.model';

export const REQUIRES_SECURITY_PROFILE = new HttpContextToken<boolean>(() => false);

@Injectable({ providedIn: 'root' })
export class SecurityProfileAuthTokenInterceptor implements HttpInterceptor {
  private readonly _authService = inject(AuthService);
  private readonly _spinnerService = inject(SpinnerService);
  private readonly _messageService = inject(MessageService);
  private readonly _environment = inject(APP_ENV);

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!request.context.get(REQUIRES_SECURITY_PROFILE)) {
      return next.handle(request);
    }

    const skipSpinner = request.headers.has(CustomUiHeaders.skipSpinner);
    const skipResetSpinner = request.headers.has(CustomUiHeaders.skipResetSpinner);

    if (!skipSpinner) {
      this._spinnerService.show();
    }

    return this._acquireToken().pipe(
      switchMap((token) => {
        if (token) {
          request = request.clone({
            setHeaders: {
              'X-Profile-Token': token,
            },
          });
        }

        return next.handle(request).pipe(
          catchError((error) => {
            this._showDefaultErrorToast(error);
            if (!skipResetSpinner) {
              this._spinnerService.reset();
            }
            throw error;
          }),
          finalize(() => {
            if (!skipSpinner) {
              this._spinnerService.hide();
            }
          }),
        );
      }),
    );
  }

  private _acquireToken(): Observable<string> {
    return this._authService.securityProfileAuthToken$.pipe(
      first(),
      map((token) => (token ? token : null)),
      switchMap((token) => {
        if (token && this._shouldRefresh(token)) {
          return this._authService.refreshSecurityProfileAuthToken().pipe(map((token) => token.token));
        }

        return of(token);
      }),
    );
  }

  /**
   * Shows default error toast.
   * Currently **only** for the Lead Form page (`/lead-form`).
   */
  private _showDefaultErrorToast(error: any) {
    if (!window.location.href.includes('/lead-form')) {
      return;
    }

    // Display default error toast only when the failed operation:
    // 1) Doesn't have its own custom error toast.
    // 2) Hasn't shown its custom error toast within the timeframe allocated by setTimeout().
    setTimeout(() => {
      if (document.getElementsByClassName('p-toast-message-error')?.length === 0) {
        const isClientSideError = error.error instanceof ErrorEvent;

        this._messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: isClientSideError ? `Error: ${error.error.message}` : `[Code]: ${error.status} \n\n[Message]: ${error.message}`,
          life: 5000,
        });
      }
    }, 200);
  }

  private _shouldRefresh(token: string) {
    const claims = decodeJwt<SecurityProfileAuthToken>(token);
    const expiration = claims.exp;
    const now = Math.floor(Date.now() / 1000);

    const isExpired = now > expiration;
    if (isExpired) {
      return true;
    }

    const thiryMinutes = 30 * 60 * 1_000;
    const shouldProactivelyRefresh = now - thiryMinutes > expiration;
    if (shouldProactivelyRefresh) {
      return true;
    }

    return false;
  }
}
